import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "wasp/client/auth";
import {
  createLocation,
  getAllLocations,
  getLocationTypesForDropdown,
} from "wasp/client/operations";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { ArrowLeft, MapPin } from "lucide-react";
import Breadcrumb from "../../admin/layout/Breadcrumb";
import DefaultLayout from "../../admin/layout/DefaultLayout";
import { useQuery } from "wasp/client/operations";

const CreateLocationPage = () => {
  const { data: user } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    typeId: "", // Changed from type to typeId
    isPublic: false,
    allowPublicSubscription: true,
    defaultVisibility: "VERIFIED",
    publicDescription: "",
    address: "",
    city: "",
    state: "",
    country: "",
    postalCode: "",
    timezone: "UTC",
    latitude: "",
    longitude: "",
    // Hub fields
    isHub: false,
    aggregateChildContent: true,
    parentLocationId: "" as string | null,
  });

  // Fetch available parent locations (exclude hubs and self)
  const { data: allLocationsData } = useQuery(getAllLocations, { limit: 100 });
  const locationsArray = (allLocationsData as any)?.locations || [];
  const availableParents = locationsArray.filter((loc: any) => !loc.isHub);

  // Fetch location types for dropdown
  const { data: locationTypes } = useQuery(getLocationTypesForDropdown);
  const locationTypesArray = (locationTypes as any) || [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const locationData = {
        ...formData,
        typeId: formData.typeId || undefined,
        parentLocationId: formData.parentLocationId || null,
        latitude: formData.latitude ? parseFloat(formData.latitude) : undefined,
        longitude: formData.longitude
          ? parseFloat(formData.longitude)
          : undefined,
      };

      await createLocation(locationData);
      navigate("/admin/locations");
    } catch (error) {
      console.error("Error creating location:", error);
      alert("Failed to create location: " + (error as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  if (!user?.isAdmin) {
    return (
      <div className="p-4">
        <h2>Access Denied</h2>
        <p>Admin privileges required.</p>
        <p>Current user: {JSON.stringify(user, null, 2)}</p>
      </div>
    );
  }

  return (
    <DefaultLayout user={user}>
      <div className="w-full">
        <Breadcrumb pageName="Create Location" />

        <div className="flex flex-col gap-6">
          {/* Header */}
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => navigate("/admin/locations")}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Locations
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Create New Location
              </h1>
              <p className="text-gray-600">Add a new location to the system</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <Card>
              <CardHeader>
                <CardTitle>Basic Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Location Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) =>
                        handleInputChange("name", e.target.value)
                      }
                      placeholder="Enter location name"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="type">Location Type *</Label>
                    <Select
                      value={formData.typeId}
                      onValueChange={(value) =>
                        handleInputChange("typeId", value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {locationTypesArray.map((type: any) => (
                          <SelectItem key={type.id} value={type.id}>
                            {type.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="publicDescription">Public Description</Label>
                  <Textarea
                    id="publicDescription"
                    value={formData.publicDescription}
                    onChange={(e) =>
                      handleInputChange("publicDescription", e.target.value)
                    }
                    placeholder="Describe this location for public visitors"
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Location Settings */}
            <Card>
              <CardHeader>
                <CardTitle>Location Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="isPublic">Public Location</Label>
                    <p className="text-sm text-gray-600">
                      Allow public visitors to view this location
                    </p>
                  </div>
                  <Switch
                    id="isPublic"
                    checked={formData.isPublic}
                    onCheckedChange={(checked) =>
                      handleInputChange("isPublic", checked)
                    }
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="allowPublicSubscription">
                      Allow Public Subscription
                    </Label>
                    <p className="text-sm text-gray-600">
                      Allow users to subscribe without invitation
                    </p>
                  </div>
                  <Switch
                    id="allowPublicSubscription"
                    checked={formData.allowPublicSubscription}
                    onCheckedChange={(checked) =>
                      handleInputChange("allowPublicSubscription", checked)
                    }
                  />
                </div>

                <div>
                  <Label htmlFor="defaultVisibility">
                    Default Content Visibility
                  </Label>
                  <Select
                    value={formData.defaultVisibility}
                    onValueChange={(value) =>
                      handleInputChange("defaultVisibility", value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select visibility" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PUBLIC">Public</SelectItem>
                      <SelectItem value="SUBSCRIBER">
                        Subscribers Only
                      </SelectItem>
                      <SelectItem value="VERIFIED">
                        Verified Members Only
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Address Information */}
            <Card>
              <CardHeader>
                <CardTitle>Address Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="address">Street Address</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) =>
                      handleInputChange("address", e.target.value)
                    }
                    placeholder="123 Main Street"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) =>
                        handleInputChange("city", e.target.value)
                      }
                      placeholder="City"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="state">State/Province *</Label>
                    <Input
                      id="state"
                      value={formData.state}
                      onChange={(e) =>
                        handleInputChange("state", e.target.value)
                      }
                      placeholder="State/Province"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="country">Country *</Label>
                    <Input
                      id="country"
                      value={formData.country}
                      onChange={(e) =>
                        handleInputChange("country", e.target.value)
                      }
                      placeholder="Country"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="postalCode">Postal Code</Label>
                    <Input
                      id="postalCode"
                      value={formData.postalCode}
                      onChange={(e) =>
                        handleInputChange("postalCode", e.target.value)
                      }
                      placeholder="12345"
                    />
                  </div>

                  <div>
                    <Label htmlFor="timezone">Timezone</Label>
                    <Select
                      value={formData.timezone}
                      onValueChange={(value) =>
                        handleInputChange("timezone", value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select timezone" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="UTC">UTC</SelectItem>
                        <SelectItem value="America/New_York">
                          Eastern Time
                        </SelectItem>
                        <SelectItem value="America/Chicago">
                          Central Time
                        </SelectItem>
                        <SelectItem value="America/Denver">
                          Mountain Time
                        </SelectItem>
                        <SelectItem value="America/Los_Angeles">
                          Pacific Time
                        </SelectItem>
                        <SelectItem value="Europe/London">London</SelectItem>
                        <SelectItem value="Europe/Paris">Paris</SelectItem>
                        <SelectItem value="Asia/Tokyo">Tokyo</SelectItem>
                        <SelectItem value="Asia/Shanghai">Shanghai</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Coordinates */}
            <Card>
              <CardHeader>
                <CardTitle>Coordinates (Optional)</CardTitle>
                <p className="text-sm text-gray-600">
                  Used for generating unique location codes and mapping
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="latitude">Latitude</Label>
                    <Input
                      id="latitude"
                      type="number"
                      step="any"
                      value={formData.latitude}
                      onChange={(e) =>
                        handleInputChange("latitude", e.target.value)
                      }
                      placeholder="40.7128"
                    />
                  </div>

                  <div>
                    <Label htmlFor="longitude">Longitude</Label>
                    <Input
                      id="longitude"
                      type="number"
                      step="any"
                      value={formData.longitude}
                      onChange={(e) =>
                        handleInputChange("longitude", e.target.value)
                      }
                      placeholder="-74.0060"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Hub/Hierarchy Settings */}
            <Card>
              <CardHeader>
                <CardTitle>Hierarchy & Hub Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="isHub">Is Hub</Label>
                    <p className="text-sm text-gray-600">
                      Hubs aggregate content from all child locations
                    </p>
                  </div>
                  <Switch
                    id="isHub"
                    checked={formData.isHub}
                    onCheckedChange={(checked) =>
                      handleInputChange("isHub", checked)
                    }
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="aggregateChildContent">
                      Aggregate Child Content
                    </Label>
                    <p className="text-sm text-gray-600">
                      Show content from all child locations in this hub
                    </p>
                  </div>
                  <Switch
                    id="aggregateChildContent"
                    checked={formData.aggregateChildContent}
                    onCheckedChange={(checked) =>
                      handleInputChange("aggregateChildContent", checked)
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="parentLocationId">
                    Parent Location (Optional)
                  </Label>
                  <Select
                    value={formData.parentLocationId || "none"}
                    onValueChange={(v) =>
                      handleInputChange(
                        "parentLocationId",
                        v === "none" ? null : v,
                      )
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select parent..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None (Root Location)</SelectItem>
                      {availableParents.map((loc: any) => (
                        <SelectItem key={loc.id} value={loc.id}>
                          {loc.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Submit */}
            <div className="flex justify-end gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/admin/locations")}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Creating..." : "Create Location"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </DefaultLayout>
  );
};

export default CreateLocationPage;
